import { BaseConstruction } from './BaseConstruction';
import { ConstructionStep } from '../types';
import { Point } from '../objects/Point';
import { Segment } from '../objects/Segment';
import { Arc } from '../objects/Arc';
import { Angle } from '../objects/Angle';
import { DrawPoint, DrawLine, DrawArc } from '../animations/DrawPoint';
import { MoveCompass, Highlight, Fade } from '../animations/MoveCompass';

export class EquilateralTriangleConstruction extends BaseConstruction {
  public buildSteps(options: { x?: number; y?: number; size?: number } = {}): ConstructionStep[] {
    const startX = options.x ?? 250;
    const startY = options.y ?? 480;
    const sideLen = options.size ?? 250;

    const pA = { x: startX, y: startY };
    const pB = { x: startX + sideLen, y: startY };
    const pC = {
      x: startX + sideLen / 2,
      y: startY - (Math.sqrt(3) / 2) * sideLen,
    };

    const pointA = new Point(this.renderer, pA.x, pA.y, 'A');
    const pointB = new Point(this.renderer, pB.x, pB.y, 'B');
    const pointC = new Point(this.renderer, pC.x, pC.y, 'C');

    const segAB = new Segment(this.renderer, pA, pB, false);
    const segAC = new Segment(this.renderer, pA, pC, false);
    const segBC = new Segment(this.renderer, pB, pC, false);

    // arcA: center A, C is at ~-60° from A. CCW -36°→-81° = 45° arc centered on -58.5° ✓
    const arcA = new Arc(this.renderer, pA, sideLen, -Math.PI * 0.2, -Math.PI * 0.45, true, false);
    // arcB: center B, C is at ~-120° from B. CCW -99°→-144° = 45° arc centered on -121.5° ✓
    const arcB = new Arc(this.renderer, pB, sideLen, -Math.PI * 0.55, -Math.PI * 0.8, true, false);

    const angleA = new Angle(this.renderer, pA, pB, pC, '60°');
    const angleB = new Angle(this.renderer, pB, pC, pA, '60°');
    const angleC = new Angle(this.renderer, pC, pA, pB, '60°');

    [pointA, pointB, pointC, segAB, segAC, segBC, arcA, arcB, angleA, angleB, angleC].forEach((o) =>
      o.setVisible(false)
    );

    return [
      {
        id: 'step1',
        title: '1. Draw Base Segment AB',
        description: 'Draw base segment AB of length L.',
        narration: 'Step 1: Drawing baseline AB for the equilateral triangle.',
        duration: 1200,
        animations: [new DrawPoint(pointA), new DrawPoint(pointB), new DrawLine(segAB, this.ruler)],
      },
      {
        id: 'step2',
        title: '2. Draw Arc from A with Radius AB',
        description: 'Open compass to radius AB and draw arc above segment.',
        narration: 'Step 2: Placing compass at A with radius AB and drawing top arc.',
        duration: 1500,
        animations: [
          new MoveCompass(this.compass, pA, sideLen),
          new DrawArc(arcA, this.compass),
        ],
      },
      {
        id: 'step3',
        title: '3. Intersect Arc from B to Locate Vertex C',
        description: 'Move compass to B with same radius AB and intersect arc to find vertex C.',
        narration: 'Step 3: Moving compass to B and drawing intersecting arc to locate vertex C.',
        duration: 1500,
        animations: [
          new MoveCompass(this.compass, pB, sideLen),
          new DrawArc(arcB, this.compass),
          new DrawPoint(pointC),
        ],
      },
      {
        id: 'step4',
        title: '4. Draw Remaining Sides AC and BC',
        description: 'Use ruler to connect A to C and B to C.',
        narration: 'Step 4: Connecting vertices A to C and B to C with the ruler.',
        duration: 2000,
        animations: [
          new Fade(this.compass, false),
          new DrawLine(segAC, this.ruler),
          new DrawLine(segBC, this.ruler),
        ],
      },
      {
        id: 'step5',
        title: '5. Highlight Equilateral Triangle',
        description: 'Highlight triangle edges and 60° internal angles.',
        narration: 'Construction complete! All three sides and 60 degree angles are equal.',
        duration: 1500,
        animations: [
          new Fade(this.ruler, false),
          new Highlight(segAB),
          new Highlight(segAC),
          new Highlight(segBC),
          new DrawPoint(angleA as any),
          new DrawPoint(angleB as any),
          new DrawPoint(angleC as any),
        ],
      },
    ];
  }
}
